// File: src/pages/TabAccount.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PasswordStrength from '../../hooks/PasswordStrength';

function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        document.cookie.split(';').forEach(cookie => {
            const [key, val] = cookie.trim().split('=');
            if (key === name) cookieValue = decodeURIComponent(val);
        });
    }
    return cookieValue;
}

const csrftoken = getCookie('csrftoken');

/** API may return ISO (YYYY-MM-DD) or a long date string; <input type="date"> needs YYYY-MM-DD. */
function birthdateToDateInputValue(value) {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const t = Date.parse(s);
    if (Number.isNaN(t)) return '';
    const d = new Date(t);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

function ProfileTab() {
    // 1. State for each field
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [birthdate, setBirthdate] = useState('');
    const [gender, setGender] = useState(''); // 'M' or 'F'
    const [bio, setBio] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const [profilePictureUrl, setProfilePictureUrl] = useState('');     // preview URL (string)
    const [profilePictureFile, setProfilePictureFile] = useState(null); // the File object
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmError, setConfirmError] = useState('');
    const [pwFieldErrors, setPwFieldErrors] = useState({});
    const [pwSaving, setPwSaving] = useState(false);
    const [pwError, setPwError] = useState(null);
    const [pwSuccess, setPwSuccess] = useState(false);

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // 2. Fetch current profile on mount
    useEffect(() => {
        axios.get('/api/users/my-details/')
            .then(({ data }) => {
                setFirstName(data.first_name);
                setLastName(data.last_name);
                setEmail(data.email);
                setBirthdate(birthdateToDateInputValue(data.birthdate));
                setGender(data.gender || '');
                setBio(data.bio || '');
                setPhoneNumber(data.phone_number || '');
                setProfilePictureUrl(data.profile_picture || '');
            })
            .finally(() => setLoading(false));
    }, []);

    // 3. Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFieldErrors({});
        setSuccess(false);

        try {
            // 1) Create FormData instead of a plain object, so we can append the image
            const formData = new FormData();
            formData.append('first_name', firstName);
            formData.append('last_name', lastName);
            formData.append('email', email);
            formData.append('birthdate', birthdate);
            formData.append('gender', gender);
            formData.append('bio', bio);
            formData.append('phone_number', phoneNumber);

            // 2) Only append the file if the user actually picked one
            if (profilePictureFile) {
                formData.append('profile_picture', profilePictureFile);
            }

            // 3) Tell axios to send multipart/form-data
            await axios.patch('/api/users/my-details/', formData, {
                headers: {
                    'X-CSRFToken': csrftoken,
                    // Let browser set the Content‑Type including boundary
                    'Content-Type': 'multipart/form-data',
                },
            });

            setSuccess(true);
        } catch (err) {
            // unchanged: grab field errors from your DRF response
            const errs = err.response?.data || {};
            setFieldErrors(errs);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (window.PWSmeter) {
            window.PWSmeter(
                document.getElementById('new-password-input'),
                document.getElementById('pswmeter'),
                document.getElementById('pswmeter-message')
            );
        }
    }, [newPassword]);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwSaving(true);
        setPwError(null);
        setPwFieldErrors({});
        setPwSuccess(false);
        try {
            await axios.post('/api/users/change-password/', {
                old_password: currentPassword,
                new_password: newPassword,
                confirm_password: confirmPassword,
            },
                {
                    headers: { 'X-CSRFToken': csrftoken },
                }
            );
            setPwSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            // setPwError(err.response?.data?.detail || 'Failed to change password.');
            setPwFieldErrors(err.response?.data || {});
        } finally {
            setPwSaving(false);
        }
    };

    const onNewPasswordChange = e => {
        const v = e.target.value;
        setNewPassword(v);
        if (confirmPassword && v !== confirmPassword) {
            setConfirmError('Passwords do not match.');
        } else {
            setConfirmError('');
        }
    };

    const onConfirmPasswordChange = e => {
        const v = e.target.value;
        setConfirmPassword(v);
        if (newPassword && v !== newPassword) {
            setConfirmError('Passwords do not match.');
        } else {
            setConfirmError('');
        }
    };

    if (loading) return <p>Loading…</p>;

    return (
        <>
            <div className="card mb-4">
                <div className="card-header border-0 pb-0">
                    <h1 className="h5 card-title">Account Settings</h1>
                </div>
                <div className="card-body">
                    {fieldErrors.non_field_errors && fieldErrors.non_field_errors.map((msg, i) =>
                        <div key={i} className="alert alert-danger">{msg}</div>
                    )}
                    {success && <div className="alert alert-success">Profile updated!</div>}

                    <form className="row g-3" onSubmit={handleSubmit}>
                        <div className="col-sm-6">
                            <label className="form-label">First name</label>
                            <input
                                type="text"
                                className="form-control"
                                value={firstName}
                                onChange={e => setFirstName(e.target.value)}
                            />
                            {fieldErrors.first_name && fieldErrors.first_name.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        <div className="col-sm-6">
                            <label className="form-label">Last name</label>
                            <input
                                type="text"
                                className="form-control"
                                value={lastName}
                                onChange={e => setLastName(e.target.value)}
                            />
                            {fieldErrors.last_name && fieldErrors.last_name.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        <div className="col-sm-6">
                            <label className="form-label">Email</label>
                            <input
                                type="email"
                                className="form-control"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                            />
                            {fieldErrors.email && fieldErrors.email.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        <div className="col-sm-6">
                            <label className="form-label">Phone number</label>
                            <input
                                type="tel"
                                className="form-control"
                                value={phoneNumber}
                                onChange={e => setPhoneNumber(e.target.value)}
                            />
                            {fieldErrors.phone_number && fieldErrors.phone_number.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        <div className="col-sm-6">
                            <label className="form-label">Birthday</label>
                            <input
                                type="date"
                                className="form-control"
                                value={birthdate}
                                onChange={e => setBirthdate(e.target.value)}
                            />
                            {fieldErrors.birthdate && fieldErrors.birthdate.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        <div className="col-sm-6">
                            <label className="form-label">Gender</label>
                            <select
                                className="form-select"
                                value={gender}
                                onChange={e => setGender(e.target.value)}
                            >
                                <option value="">Select…</option>
                                <option value="M">Male</option>
                                <option value="F">Female</option>
                            </select>
                            {fieldErrors.gender && fieldErrors.gender.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        {/* ─── PROFILE PICTURE PREVIEW & UPLOAD ───────────────────────────────── */}
                        <div className="col-12">
                            <label className="form-label m-3">Profile Picture</label>
                            {/* show current or newly selected preview */}
                            {profilePictureUrl &&
                                <img
                                    src={profilePictureUrl}
                                    alt="Avatar preview"
                                    className="img-thumbnail mb-2"
                                    style={{ width: 100, height: 100, objectFit: 'cover' }}
                                />
                            }
                            <input
                                type="file"
                                accept="image/*"
                                className="form-control"
                                onChange={e => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        setProfilePictureFile(file);
                                        setProfilePictureUrl(URL.createObjectURL(file));
                                    }
                                }}
                            />
                            {fieldErrors.profile_picture && fieldErrors.profile_picture.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        <div className="col-12">
                            <label className="form-label">Bio</label>
                            <textarea
                                className="form-control"
                                rows="4"
                                maxLength={500}
                                value={bio}
                                onChange={e => setBio(e.target.value)}
                            />
                            {fieldErrors.bio && fieldErrors.bio.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        <div className="col-12 text-end">
                            <button
                                type="submit"
                                className="btn btn-sm btn-primary mb-0"
                                disabled={saving}
                            >
                                {saving ? 'Saving…' : 'Save changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="card">
                <div className="card-header border-0 pb-0">
                    <h5 className="card-title">Change your password</h5>
                    <p className="mb-0">Enter your current password and a new password to update.</p>
                </div>
                <div className="card-body">
                    {pwError && <div className="alert alert-danger">{pwError}</div>}
                    {pwFieldErrors.non_field_errors?.map((msg, i) =>
                        <div key={i} className="alert alert-danger">{msg}</div>
                    )}
                    {pwSuccess && <div className="alert alert-success">Password changed!</div>}
                    <form className="row g-3" onSubmit={handleChangePassword}>
                        {/* Current password — user enters to confirm; optional reveal */}
                        <div className="col-12">
                            <label className="form-label" htmlFor="profile-current-password">Current password</label>
                            <div className="input-group">
                                <input
                                    id="profile-current-password"
                                    type={showCurrent ? 'text' : 'password'}
                                    className="form-control"
                                    value={currentPassword}
                                    onChange={e => setCurrentPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowCurrent(s => !s)}
                                    aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
                                    aria-pressed={showCurrent}
                                >
                                    <i className={showCurrent ? 'bi bi-eye' : 'bi bi-eye-slash'} aria-hidden="true" />
                                </button>
                            </div>
                            {pwFieldErrors.old_password?.map((msg, i) =>
                                <div key={i} className="invalid-feedback d-block">{msg}</div>
                            )}
                        </div>

                        {/* New password */}
                        <div className="col-12">
                            <label className="form-label">New password</label>
                            <div className="input-group">
                                <input
                                    id="new-password-input"
                                    type={showNew ? 'text' : 'password'}
                                    className="form-control"
                                    value={newPassword}
                                    onChange={onNewPasswordChange}
                                    autoComplete="new-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowNew(s => !s)}
                                    aria-label={showNew ? 'Hide new password' : 'Show new password'}
                                    aria-pressed={showNew}
                                >
                                    <i className={showNew ? 'bi bi-eye' : 'bi bi-eye-slash'} aria-hidden="true" />
                                </button>
                            </div>
                            <PasswordStrength
                                inputId="new-password-input"
                                containerId="password-strength-container"
                                messageId="pswmeter-message"
                            />
                        </div>

                        {/* Confirm password */}
                        <div className="col-12">
                            <label className="form-label">Confirm password</label>
                            <div className="input-group">
                                <input
                                    id="confirm-password-input"
                                    type={showConfirm ? 'text' : 'password'}
                                    className="form-control"
                                    value={confirmPassword}
                                    onChange={onConfirmPasswordChange}
                                    autoComplete="new-password"
                                    required
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowConfirm(s => !s)}
                                    aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                                    aria-pressed={showConfirm}
                                >
                                    <i className={showConfirm ? 'bi bi-eye' : 'bi bi-eye-slash'} aria-hidden="true" />
                                </button>
                            </div>
                            {confirmError && (
                                <div className="invalid-feedback d-block">
                                    {confirmError}
                                </div>
                            )}
                        </div>

                        <div className="col-12 text-end">
                            <button
                                type="submit"
                                className="btn btn-primary mb-0"
                                disabled={pwSaving || !!confirmError}
                            >
                                {pwSaving ? 'Updating…' : 'Update password'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

export default ProfileTab;
